import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import ProfitCenterLinksProduct from 'components/ProfitCenterLinksProduct/ProfitCenterLinksProduct';
import SourcingConnections from 'components/SourcingConnections/SourcingConnections';
import Tabs from 'components/Tabs/Tabs';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  PRODUCT_TYPE, PROFIT_CENTER_LINKS, VALUE_CHAIN_LIST, VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useAiSubScreenContext } from 'contexts/AiScreenContext';
import UnitRefsProvider from 'contexts/UnitRefsContext';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainColumns from 'hooks/useValueChainColumns';
import useValueChainPosition from 'hooks/useValueChainPosition';
import { getProductModalMockValues } from 'mocks/companyMapModalMocks';
import ProductGeneralFields from 'pages/UserPages/CreateProduct/components/ProductGeneralFields';
import { useAppDispatch } from 'store/hooks/useApp';
import { useEnumQuery, useGetProductCategoriesQuery, useUidNameTuplesQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectUidNameTuples } from 'store/slices/dataSets/selectors';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import {
  useCreateProductMutation, useGetBoardColumnNamesQuery, useLazyGetValueChainMapBoardQuery,
  useLazyGetValueChainMapBusinessModelQuery, useUpdateProductMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getRequestDataForProductUpdate from 'utils/formHelpers/getRequestDataForProductUpdate';
import { getRelationTypeByProductType, getRelationTypeByValueChain } from 'utils/formHelpers/productTypeValueChain';
import { extractApiErrorMessage } from 'utils/helpers';

import useCreateProductForm from './hooks/useCreateProductForm';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IProduct from 'store/slices/valueChainMap/interfaces/IProduct';
import type { TCreateProductSchema } from 'utils/validators';
import type IProductModal from './interfaces/IProductModal';

import styles from './scss/ProductModal.module.scss';

// The modal body scrolls, so the portalled unit pickers clamp to it rather than the window.
const LINKS_BOUNDARY_SELECTOR = '[class*="OwnOperationModalFormWrapper"]';

const TAB_GENERAL = 0;
const TAB_SOURCING = 1;
const TAB_FINANCIALS = 2;

// Hoisted so the sourcing picker's memos are not invalidated by a fresh array every render.
const OUTPUT_SOURCE_TYPES = [VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET];

function ProductModal({
  closeProfitCenterModal,
  valueChainMapId,
  setSelectedProduct,
  currentTargetSwimlaneId,
  selectedProduct,
  valueChainForm,
}: IProductModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setFieldsErrors } = useFieldsErrors();
  const placeholder = selectedProduct?.id ? t('common.notProvided') : '';
  const modalRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(TAB_GENERAL);

  const tabs = [
    { id: TAB_GENERAL, name: t('products.selectionsGeneral') },
    { id: TAB_SOURCING, name: t('products.selectionsSourcing') },
    { id: TAB_FINANCIALS, name: t('products.selectionsFinancials') },
  ];

  const [createProduct, { isLoading: isCreateProductLoading }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdateProductLoading }] = useUpdateProductMutation();
  const [fetchVCBusinessModel] = useLazyGetValueChainMapBusinessModelQuery();
  const [fetchVCBoard] = useLazyGetValueChainMapBoardQuery();
  const isUniqueIdentifierEditable = selectedProduct?.id
    ? !selectedProduct.uniqueIdentifier || selectedProduct.uniqueIdentifier === '0'
    : true;

  const generateTitle = () => (selectedProduct?.id
    ? t('products.editInputProduct')
    : t('products.addNewInputProduct'));

  // Open over the map: the assistant is told which unit is being edited, or that a new one is.
  useAiSubScreenContext({
    name: generateTitle(),
    customData: {
      unitType: VALUE_CHAIN_UNIT_TYPES.PRODUCT,
      id: selectedProduct?.id,
      name: selectedProduct?.name,
    },
  });

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const {
    data: boardColumnNamesData,
    isLoading: isBoardColumnNamesLoading,
  } = useGetBoardColumnNamesQuery({ valueChainMapId });

  const { data: productCategories, isLoading: isProductCategoriesLoading } = useGetProductCategoriesQuery();
  const productCategoriesOptions = productCategories?.data?.items.map((category) => ({
    label: category.name,
    value: `${category.id}`,
  }));

  const handleGetCurrentColumn = (id?: number) => {
    let currentColumn: { id: number, name: string, boardName: string } = {
      id: 0,
      name: '',
      boardName: '',
    };
    if (id) {
      const currentColumnName = boardColumnNamesData?.data?.find(
        (column) => column?.columnId === id,
      )?.columnName || '';
      const [boardName, ...sectionNameParts] = currentColumnName.split('.');
      currentColumn = {
        id,
        boardName: boardName?.trim() || '',
        name: sectionNameParts.join('.').trim(),
      };
    }
    return currentColumn;
  };

  const { data: uidNameTuples, isLoading: isNaceUidNameTuplesLoading } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.volumeQtyUnit,
      DATA_SETS_ENUM_TYPES.relianceOnFreeNaturalResources, DATA_SETS_ENUM_TYPES.relianceOnFreeSocialResources,
      DATA_SETS_ENUM_TYPES.criticalityRating, DATA_SETS_ENUM_TYPES.upstreamTier,
      DATA_SETS_ENUM_TYPES.valueChainSection],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const isPageLoading = isEnumLoading || isCreateProductLoading || isUpdateProductLoading
    || isBoardColumnNamesLoading || isNaceUidNameTuplesLoading || isProductCategoriesLoading;

  const currentColumn = handleGetCurrentColumn(currentTargetSwimlaneId);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    profitCenterIds,
    formState,
  } = useCreateProductForm({
    recordId: selectedProduct?.id ? String(selectedProduct.id) : '',
    isPageLoading,
    valueChainMapId,
    valueChainSectionCurrent: currentColumn?.name,
    productCategoriesOptions,
  });

  useEffect(() => {
    const currentBoardName = currentColumn?.boardName;
    if (currentBoardName && !isPageLoading) {
      setValue(VALUE_CHAIN_LIST, {
        value: currentBoardName,
        label: currentBoardName,
      });
    }
  }, [currentColumn?.boardName, isPageLoading, setValue]);

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value, valueChainMapId);

  const { dateOpened, onDateOpenedChange } = useDataSetsDate<TCreateProductSchema>({ watch, setValue });

  const { getColumnId } = useValueChainColumns({
    enumData,
    boardColumnNamesData,
  });

  const { scrollToErrors } = useScrollToError({
    formState,
    scrollContainerRef: modalRef,
    scrollContainerSelector: '[class*="OwnOperationModalFormWrapper"]',
  });

  // Both product types are sourced through goods-supplier connections; only the candidate
  // sources differ. An output product is manufactured by the balance sheet assets of the profit
  // centres it belongs to, so its picker is narrowed to those; an input product is supplied
  // from anywhere on the map.
  const isOutputSourced = getRelationTypeByProductType(watch(PRODUCT_TYPE)?.value)
    === VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET;
  const profitCentreLinks = watch(PROFIT_CENTER_LINKS);
  const productProfitCentreUIDs = useMemo(
    () => (profitCentreLinks || []).map((link) => link.profitCenterId).filter(Boolean),
    [profitCentreLinks],
  );
  const isMockFillVisible = !selectedProduct?.id && process.env.REACT_APP_API?.includes('api.dev');
  const unitOptions = (profitCenterIds || uidNameTuples) as Record<string, IOption[]> | undefined;

  const onFillMockData = () => {
    const relationType = getRelationTypeByValueChain(watch(VALUE_CHAIN_LIST)?.value);

    reset({
      ...watch(),
      ...getProductModalMockValues({
        enumData,
        profitCenterOptions: unitOptions?.[UID_NAME_TUPLES.profitCenter],
        unitLinkOptions: relationType ? unitOptions?.[relationType] : [],
        relationType,
        productCategoriesOptions,
      }),
    });
  };

  // Links carry their own record type, so the section no longer decides what is sent.
  const getProductRequestData = (values: TCreateProductSchema, recordId?: string) => (
    getRequestDataForProductUpdate({
      values,
      recordId,
      companyCurrency,
      valueChainForm,
      includeUniqueIdentifier: isUniqueIdentifierEditable,
    })
  );

  const resolveProductMutationColumnId = (): string | undefined => {
    const fromBoardColumns = getColumnId(
      `${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`,
    );
    const resolved = fromBoardColumns ?? currentTargetSwimlaneId;
    if (resolved === undefined || resolved === null) {
      return undefined;
    }
    return String(resolved);
  };

  const onCreateCustomerSubmit = handleSubmit((values) => {
    if (selectedProduct?.id) {
      updateProduct({
        data: getProductRequestData(values, `${selectedProduct.id}`),
        valueChainMapId,
        columnId: resolveProductMutationColumnId(),
      })
        .unwrap()
        .then(() => {
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    } else {
      createProduct({
        data: getProductRequestData(values),
        valueChainMapId,
        columnId: resolveProductMutationColumnId(),
      })
        .unwrap()
        .then(({ data }) => {
          setSelectedProduct({ id: data.id, uniqueIdentifier: data.uniqueIdentifier } as IProduct);
          dispatch(setDataSetsState({
            selectedInputProduct: { label: data.uniqueIdentifier, value: data.uniqueIdentifier },
          }));
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    }
  });

  const sourcingTabContent = (
    <DataSetsPanel title={t('products.selectionsSourcing')} className={styles.fullWidth}>
      <SourcingConnections
        control={control}
        watch={watch}
        setValue={setValue}
        enumData={enumData}
        sourceTypes={isOutputSourced ? OUTPUT_SOURCE_TYPES : undefined}
        profitCentreUIDs={isOutputSourced ? productProfitCentreUIDs : undefined}
        label={isOutputSourced ? t('products.fields.manufacturedBy') : undefined}
        boundarySelector={LINKS_BOUNDARY_SELECTOR}
      />
    </DataSetsPanel>
  );

  return (
    <Modal
      open
      isButtonsVisible={false}
      className={styles.modal}
      title={generateTitle()}
      titleAction={isMockFillVisible ? (
        <ModalMockFillButton
          onClick={onFillMockData}
          disabled={isPageLoading}
        />
      ) : null}
    >
      <UnitRefsProvider valueChainMapId={valueChainMapId}>
        <div ref={modalRef}>
          <Tabs data={tabs} activeTab={activeTab} onClickTab={setActiveTab} />
          <div>
            <div className={styles.OwnOperationModalFormWrapper}>
              <Loader isPageLoading={isPageLoading} isFullLoader={false} />
              {activeTab === TAB_GENERAL ? (
                <ProductGeneralFields
                  control={control}
                  enumData={enumData}
                  dateOpened={dateOpened}
                  onDateOpenedChange={onDateOpenedChange}
                  recordId={selectedProduct?.id ? String(selectedProduct.id) : ''}
                  isUniqueIdentifierEditable={isUniqueIdentifierEditable}
                  handleGetVcOptions={handleGetVcOptions}
                  vcOptionsList={vcOptionsList}
                  listPosition={listPosition}
                  setValue={setValue}
                  productCategoriesOptions={productCategoriesOptions}
                  isProductTypeDisabled
                  vcDisabled
                />
              ) : null}
              {activeTab === TAB_SOURCING ? sourcingTabContent : null}
              {activeTab === TAB_FINANCIALS ? (
                <DataSetsPanel title={t('products.selectionsFinancials')} className={styles.fullWidth}>
                  <ProfitCenterLinksProduct
                    control={control}
                    profitCenterIds={profitCenterIds}
                    placeholder={placeholder}
                    watch={watch}
                    setValue={setValue}
                  />
                </DataSetsPanel>
              ) : null}
            </div>
          </div>
          <div className={styles.buttons}>
            <Button
              title={t('common.cancel')}
              labelKey="common.cancel"
              variant={BUTTON_VARIANT.cancel}
              onClick={closeProfitCenterModal}
            />
            <div className={styles.buttonsRight}>
              {selectedProduct?.id ? (
                <Button
                  title={t('common.save')}
                  labelKey="common.save"
                  onClick={onCreateCustomerSubmit}
                  isLoading={isPageLoading}
                />
              ) : null}
              {!selectedProduct?.id ? (
                <Button
                  title={t('common.create')}
                  labelKey="common.create"
                  onClick={onCreateCustomerSubmit}
                  isLoading={isPageLoading}
                />
              ) : null}
            </div>
          </div>
        </div>
      </UnitRefsProvider>
    </Modal>
  );
}

export default ProductModal;
